import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import FlowStateDiagnostic from './components/FlowStateDiagnostic';
import VideoPortal from './components/VideoPortal';
import { GoogleWorkspaceReferralButton } from './components/GoogleWorkspaceReferralButton';

gsap.registerPlugin(ScrollTrigger);

// ── Portfolio Data: 11-Agent Workforce ──────────────────────────────────────
const portfolioCards = [
  {
    title: 'Automating the Revenue Leak',
    problem: 'A $400K/yr coaching firm was losing 18 hrs/week to manual client onboarding, follow-up sequences, and contract chasing — costing an estimated $120K in opportunity cost annually.',
    solution: '3-agent onboarding workforce: an Intake Agent, a Contract & Payment Agent, and a Welcome Sequence Agent — fully automated via custom API bridges and encrypted client portals.',
    outcome: 'Zero manual onboarding. 18 hours returned to the founder per week. Client activation time reduced from 5 days to under 4 hours.',
  },
  {
    title: 'Replacing a 3-Person Admin Team',
    problem: 'A scaling consultancy had $180K/yr in overhead costs for tasks that were 80% repetitive: scheduling, reporting, data entry, and internal communications.',
    solution: '4-agent administrative workforce: a Calendar & Scheduling Agent, a Reporting Agent, a Data Hygiene Agent, and an Internal Comms Agent — all coordinated through a central orchestration layer.',
    outcome: '$140K in annual overhead eliminated. Leadership operating at strategic capacity instead of operational capacity 100% of the time.',
  },
  {
    title: 'Building an AI-Integrated Website & Content Engine',
    problem: 'A high-ticket service brand had a static website and fragmented content across 4 platforms — no automation, no AI integration, and $60K/yr in agency fees with declining organic reach and inconsistent messaging.',
    solution: '4-agent content & web workforce: a Research Agent, a Copy Agent, a Web Update Agent, and a Publishing Agent — coordinated weekly from a single founder brief, with AI-native site updates triggered automatically.',
    outcome: '40 pieces of cross-platform content per week. Agency contract terminated. Website self-updated via AI workflow. Organic reach increased 340% in 90 days.',
  },
];

function App() {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsLoading(false), 2800);

    // Canvas fades as user enters solid sections
    const canvasFade = gsap.to('.canvas-container-fixed', {
      opacity: 0.06,
      scrollTrigger: {
        trigger: '.section-video-portal',
        start: 'top bottom',
        end: 'top center',
        scrub: 1.2,
      },
    });

    return () => {
      clearTimeout(timer);
      canvasFade.scrollTrigger?.kill();
      canvasFade.kill();
    };
  }, []);

  const scrollTo = (selector: string) =>
    document.querySelector(selector)?.scrollIntoView({ behavior: 'smooth' });

  return (
    <div className="relative-container">

      {/* ── LUXURY LOADER ─────────────────────────────────────── */}
      {isLoading && (
        <div className="luxury-loader">
          <img
            src="/lrc_logo.png"
            alt="LRC Systems"
            style={{
              width: '220px',
              height: 'auto',
              marginBottom: '2rem',
              opacity: 0,
              animation: 'fadeUp 1.2s cubic-bezier(0.16, 1, 0.3, 1) forwards',
              filter: 'drop-shadow(0 0 18px rgba(124, 168, 176, 0.3))',
            }}
          />
          <div className="loader-bar-container">
            <div className="loader-bar" />
          </div>
        </div>
      )}



      {/* ── SCROLLABLE CONTENT ────────────────────────────────── */}
      <main className="scroll-wrapper">

        <section className="section-hero">
          <div style={{
            zIndex: 10,
            maxWidth: '860px',
            padding: '3rem 3rem 2.5rem',
            textAlign: 'center',
            /* Frosted glass panel — particles show through the sides and below */
            background: 'rgba(5, 5, 7, 0.80)',
            backdropFilter: 'blur(24px)',
            WebkitBackdropFilter: 'blur(24px)',
            border: '1px solid rgba(255, 255, 255, 0.05)',
            borderRadius: '6px',
            boxShadow: '0 32px 80px rgba(0, 0, 0, 0.6), inset 0 1px 0 rgba(255,255,255,0.04)',
          }}>
            <span className="track-tag" style={{ animation: 'fadeUp 1.5s ease forwards', opacity: 0 }}>
              AI-Native Systems Architecture
            </span>
            {/* LRC Logo — placement preserved exactly */}
            <img
              src="/lrc_logo.png"
              alt="LRC Systems Logo"
              style={{
                width: 'clamp(260px, 55vw, 580px)',
                height: 'auto',
                display: 'block',
                margin: '1.5rem auto 2rem auto',
                filter: 'drop-shadow(0 0 28px rgba(124, 168, 176, 0.25)) drop-shadow(0 0 10px rgba(178, 100, 230, 0.35))',
                animation: 'fadeUp 2s ease forwards',
                opacity: 1,
              }}
            />
            <p style={{
              fontSize: '1.2rem',
              marginBottom: '2.5rem',
              color: 'rgba(232, 230, 240, 0.92)',
              maxWidth: '600px',
              margin: '0 auto 2.5rem auto',
              textShadow: '0 1px 8px rgba(0,0,0,0.8)',
            }}>
              Architecting operational sovereignty for elite digital enterprises through AI-native systems design.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
              <button className="luxury-btn" onClick={() => scrollTo('.section-diagnostic')}>
                Begin Diagnostic
              </button>
              <button className="luxury-btn" onClick={() => scrollTo('.section-video-portal')}
                style={{ borderColor: 'rgba(255,255,255,0.2)', color: 'var(--color-text-secondary)' }}>
                Watch the System
              </button>
            </div>
          </div>
        </section>


        {/* ══ TRANSFORMATION TRACKS (3D Scroll Canvas) ══════════ */}
        <div ref={scrollContainerRef} className="relative-container" id="scroll-container">

          {/* TRACK 01 — THE SANCTUARY AUDIT */}
          <section className="track-section">
            <div className="track-content">
              <span className="track-tag">Track 01 // The Sanctuary Audit</span>
              <h2 className="track-title">
                Where Clarity <span>Begins</span>
              </h2>
              <p className="track-desc">
                Before we build, we listen. The Sanctuary Audit is a deep-immersion systems diagnostic — a safe, high-integrity space where we map every operational friction point, energy drain, and revenue leak across your entire business architecture.
              </p>
              <ul className="sovereign-list">
                <li>Full operational audit across all digital touchpoints</li>
                <li>Revenue leak identification & priority mapping</li>
                <li>Nervous-system-informed workflow analysis</li>
                <li>Personalized FlowState Systems Blueprint delivered</li>
              </ul>
              <button className="luxury-btn" onClick={() => scrollTo('.section-diagnostic')}
                style={{ marginTop: '2rem' }}>
                Begin Your Sanctuary Audit →
              </button>
            </div>
          </section>

          {/* TRACK 02 — THE SOMATIC LAUNCHPAD */}
          <section className="track-section">
            <div className="track-content">
              <span className="track-tag">Track 02 // The Somatic Launchpad</span>
              <h2 className="track-title">
                Where Systems <span>Come Alive</span>
              </h2>
              <p className="track-desc">
                The Somatic Launchpad is a 90-day high-touch build engagement. We engineer your bespoke AI workforce from the ground up — custom agents, automated pipelines, and integrated systems designed to breathe life into your vision.
              </p>
              <ul className="sovereign-list">
                <li>Custom multi-agent workforce (up to 11 coordinated agents)</li>
                <li>Private LLM integration with your brand voice and data</li>
                <li>Zero-friction client onboarding & delivery automation</li>
                <li>Intelligent revenue operations & reporting systems</li>
              </ul>
            </div>
          </section>

          {/* TRACK 03 — THE SOVEREIGN ECOSYSTEM */}
          <section className="track-section">
            <div className="track-content">
              <span className="track-tag">Track 03 // The Sovereign Ecosystem</span>
              <h2 className="track-title">
                Where You Become <span>Untouchable</span>
              </h2>
              <p className="track-desc">
                The Sovereign Ecosystem is your complete operational infrastructure — a self-sustaining, self-healing network of AI systems, data pipelines, and digital assets that generate revenue, serve clients, and scale with zero founder dependency.
              </p>
              <ul className="sovereign-list">
                <li>Full-stack operational autonomy across all revenue channels</li>
                <li>Self-correcting data pipelines & AI quality assurance layers</li>
                <li>Quarterly architecture evolution & scaling protocols</li>
                <li>Sovereign-tier access: priority support & strategic review</li>
              </ul>
            </div>
          </section>
        </div>

        {/* ══ VIDEO PORTAL ══════════════════════════════════════ */}
        <section className="section-video-portal">
          <div className="video-portal-text">
            <span className="track-tag">LRC FLOW Framework</span>
            <h2>
              The Architecture <em style={{ fontFamily: 'var(--font-serif)' }}>in Motion</em>
            </h2>
            <p>
              The LRC FLOW Framework is our operating philosophy of transparency behind every system we build. Watch how we translate operational chaos into a nervous-system-regulating, revenue-generating machine — without sacrificing the humanity of your brand.
            </p>
            <p style={{ fontSize: '0.9rem', color: 'rgba(169,167,175,0.7)', fontStyle: 'italic' }}>
              Click the portal to watch. No autoplay. No interruption. Just signal when you're ready.
            </p>
          </div>
          <div className="video-portal-frame">
            <VideoPortal />
          </div>
        </section>

        {/* ══ PORTFOLIO — 11-AGENT WORKFORCE ═══════════════════ */}
        <section className="section-portfolio">
          <div className="section-portfolio-header">
            <span className="track-tag">11-Agent Workforce</span>
            <h2>Results That <em style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic' }}>Speak</em></h2>
            <p>
              Every case study below represents a real business transformation — not a technical specification. These are the outcomes that matter: time reclaimed, overhead eliminated, and revenue unlocked.
            </p>
          </div>
          <div className="portfolio-grid">
            {portfolioCards.map((card, i) => {
              // Per-card accent colours matching the cyan→orchid→mint gradient
              const topbars = [
                'linear-gradient(90deg, rgba(124, 168, 176,0.8), rgba(162, 204, 169,0.6))',
                'linear-gradient(90deg, rgba(162, 204, 169,0.8), rgba(124, 168, 176,0.6))',
                'linear-gradient(90deg, rgba(120,240,180,0.8), rgba(124, 168, 176,0.6))',
              ];
              return (
                <div className="pso-card" key={i}>
                  {/* Futuristic accent bar */}
                  <div className="pso-card-topbar" style={{ background: topbars[i] }} />
                  <div className="pso-card-inner">
                    <span className="pso-card-number">0{i + 1}</span>
                    <h3 className="pso-title">{card.title}</h3>
                    <div className="pso-section">
                      <span className="pso-label problem">Problem</span>
                      <p>{card.problem}</p>
                    </div>
                    <div className="pso-section">
                      <span className="pso-label solution">Solution</span>
                      <p>{card.solution}</p>
                    </div>
                    <div className="pso-section outcome">
                      <span className="pso-label outcome">Outcome</span>
                      <p>{card.outcome}</p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {/* SaaS Portfolio Highlight Card */}
          <div style={{
            marginTop: '6rem',
            padding: '3rem',
            background: 'rgba(5, 5, 7, 0.85)',
            backdropFilter: 'blur(32px)',
            border: '1px solid rgba(124, 168, 176, 0.25)',
            borderRadius: '4px',
            textAlign: 'center',
            boxShadow: '0 24px 64px rgba(0, 0, 0, 0.6), 0 0 40px rgba(124, 168, 176, 0.03)'
          }}>
            <span className="track-tag" style={{ color: '#7ca8b0' }}>SaaS Application Suite</span>
            <h3 style={{ fontSize: '2.2rem', marginTop: '1rem', marginBottom: '1.2rem', fontFamily: 'var(--font-serif)', color: 'var(--color-text-primary)' }}>
              Explore Our Custom <em>Client Applications</em>
            </h3>
            <p style={{ maxWidth: '600px', margin: '0 auto 2.5rem auto', fontSize: '1.05rem', color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
              We design and build bespoke, high-performance SaaS applications to automate inventory tracking, patient scheduling, intake forms, and clinical data systems. Explore our active suite, beginning with QRSafe.
            </p>
            <Link to="/apps" className="luxury-btn" style={{ textDecoration: 'none', display: 'inline-block' }}>
              Launch SaaS Portfolio →
            </Link>
          </div>
        </section>

        {/* ══ FLOWSTATE DIAGNOSTIC ══════════════════════════════ */}
        <section className="section-diagnostic">
          <div className="diagnostic-inner">
            <div className="diagnostic-pitch">
              <span className="track-tag">Begin Here</span>
              <h2>
                Your <em style={{ fontFamily: 'var(--font-serif)', fontStyle: 'italic' }}>FlowState</em><br />Diagnostic
              </h2>
              <p>
                This is not a form. This is the beginning of a strategic consultation — a protected space where you articulate exactly what's working, what's broken, and what sovereign operations look like for your business.
              </p>
              <p>
                Latoya personally reviews every submission and responds within 48 hours with a tailored systems recommendation.
              </p>
              <ul className="diagnostic-assurances">
                <li>No pitch decks. No sales pressure. Pure strategic dialogue.</li>
                <li>Every submission is held in strict confidence.</li>
                <li>You will receive a personalized Systems Blueprint — regardless of next steps.</li>
                <li>This process is designed to feel like a consultation, not a transaction.</li>
              </ul>
            </div>
            <div className="diagnostic-form-card">
              <FlowStateDiagnostic />
            </div>
          </div>
        </section>

        {/* ══ FOUNDER PROFILE ═══════════════════════════════════ */}
        <section className="section-founder">
          <div className="founder-image-container" id="founder-parallax">
            <img
              src="/founder.png"
              alt="Latoya Carter — Principal Architect & Founder of LRC Systems"
              id="founder-portrait"
              loading="lazy"
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                objectPosition: 'center top',
                display: 'block',
                transition: 'transform 0.15s ease-out',
                willChange: 'transform',
              }}
              onMouseMove={(e) => {
                const rect = (e.currentTarget.parentElement as HTMLElement).getBoundingClientRect();
                const cx = (e.clientX - rect.left) / rect.width - 0.5;
                const cy = (e.clientY - rect.top) / rect.height - 0.5;
                (e.currentTarget as HTMLImageElement).style.transform =
                  `scale(1.04) translate(${cx * -12}px, ${cy * -8}px)`;
              }}
              onMouseLeave={(e) => {
                (e.currentTarget as HTMLImageElement).style.transform = 'scale(1) translate(0,0)';
              }}
            />
          </div>

          <div className="founder-info">
            <span className="track-tag">Architect Authority</span>
            <div className="founder-quote">
              "True power is not in owning the tools, but in owning the architecture. Systems sovereignty is the final frontier of digital independence."
            </div>
            <h3 style={{ fontSize: '2rem', marginBottom: '0.2rem', fontFamily: 'var(--font-sans)', fontWeight: 600 }}>La' Toya Renee Carter</h3>
            <p style={{ fontStyle: 'italic', fontFamily: 'var(--font-serif)', color: 'var(--color-orchid)', marginBottom: '2.5rem' }}>
              Principal Architect & Founder, LRC Digital
            </p>
            <p style={{ fontSize: '0.95rem', marginBottom: '2.5rem' }}>
              Specializing in re-engineering enterprise-scale systems. We replace chaotic software stacks with sleek, AI-native architectures — designed for long-term sovereignty, not short-term productivity hacks.
            </p>
            <button className="luxury-btn" onClick={() => scrollTo('.section-diagnostic')}>
              Begin the Diagnostic
            </button>
          </div>
        </section>

        {/* ══ FOOTER ════════════════════════════════════════════ */}
        <footer className="footer-main" style={{ flexDirection: 'column', gap: '2rem', padding: '3rem 10%' }}>
          <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1.5rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
              <img src="/lrc_logo.png" alt="LRC Systems" style={{ height: '36px', width: 'auto', filter: 'drop-shadow(0 0 8px rgba(124, 168, 176, 0.2))' }} />
              <span style={{ fontFamily: 'var(--font-raleway)', fontWeight: 800, letterSpacing: '0.18em', fontSize: '0.95rem', color: 'var(--color-text-secondary)', textTransform: 'uppercase' }}>Systems</span>
            </div>
            <nav style={{ display: 'flex', gap: '2rem', flexWrap: 'wrap', alignItems: 'center' }}>
              <Link to="/blog" style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: '#a9a7af', textDecoration: 'none' }}>Flow Theory Blog</Link>
              <Link to="/privacy" style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: '#a9a7af', textDecoration: 'none' }}>Privacy Policy</Link>
              <Link to="/terms" style={{ fontFamily: 'var(--font-mono)', fontSize: '0.72rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: '#a9a7af', textDecoration: 'none' }}>Terms of Service</Link>
            </nav>
          </div>
          <div style={{ width: '100%', height: '1px', background: 'rgba(255,255,255,0.04)' }} />
          
          {/* Google Workspace Referral Program Affiliate Link */}
          <div style={{ width: '100%', display: 'flex', justifyContent: 'center', margin: '1rem 0' }}>
            <GoogleWorkspaceReferralButton />
          </div>

          <div style={{ width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
            <div className="footer-copy">
              &copy; {new Date().getFullYear()} LRC Digital, LLC. All Rights Reserved.
            </div>
            <span style={{ fontFamily: 'var(--font-mono)', fontSize: '0.65rem', letterSpacing: '0.15em', color: 'rgba(169,167,175,0.3)' }}>POWERED BY LRC FLOW FRAMEWORK</span>
          </div>
        </footer>


      </main>
    </div>
  );
}

export default App;
